import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Paperclip, Image as ImageIcon } from "lucide-react";
import api, { inr, fmtDate, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { AdminLeaderboard } from "./AdminLeaderboard";

const inputCls = "bg-transparent border border-white/10 px-3 py-2 text-xs text-white placeholder:text-white/30 gold-focus";

const RuleCard = ({ rule, onSaved }) => {
  const [form, setForm] = useState(rule);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/referral", { enabled: form.enabled, percent: Number(form.percent) });
      onSaved(data);
      toast.success(`Referral bonus rule saved — ${data.percent}% ${data.enabled ? "active" : "paused"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap items-center gap-4 bg-[#0A0A0A] border border-[#D4AF37]/30 px-6 py-4" data-testid="referral-rule-form">
      <div className="flex items-center gap-2 mr-auto">
        <Gift size={14} className="text-[#D4AF37]" />
        <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Referral bonus rule</p>
      </div>
      <label className="flex items-center gap-2 text-xs text-white/60">
        Bonus
        <input data-testid="referral-percent-input" type="number" step="0.5" min="0.5" max="50" value={form.percent} onChange={(e) => setForm({ ...form, percent: e.target.value })} className={`${inputCls} w-20 text-center`} />
        % of friend's first approved investment
      </label>
      <label className="flex items-center gap-2 text-xs text-white/60">
        <input data-testid="referral-enabled-checkbox" type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-[#D4AF37]" /> Enabled
      </label>
      <button data-testid="referral-rule-save" disabled={busy} className="border border-[#D4AF37] text-[#D4AF37] px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">Save Rule</button>
    </form>
  );
};

const BonusRow = ({ b, onCredited }) => {
  const [ref, setRef] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const credit = async () => {
    setBusy(true);
    try {
      let proof = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        proof = (await api.post(`/admin/referral-bonuses/${b.id}/proof`, fd)).data.path;
      }
      const { data } = await api.post(`/admin/referral-bonuses/${b.id}/credit`, { reference: ref, proof });
      onCredited(data);
      toast.success(`${inr(data.bonus_amount)} credited to ${data.referrer_name}${proof ? " with transfer proof" : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 last:border-0 items-center" data-testid={`bonus-row-${b.id}`}>
      <div className="col-span-3">
        <p className="text-sm text-white">{b.referrer_name}</p>
        <p className="text-xs text-white/35 mt-1">{b.referrer_email}</p>
      </div>
      <div className="col-span-3 text-xs text-white/60">
        <p>Referred <span className="text-white">{b.referred_name}</span></p>
        <p className="mt-1">{inr(b.investment_amount)} · {b.plan_title}</p>
      </div>
      <div className="col-span-2">
        <p className="text-sm text-[#D4AF37]" data-testid={`bonus-amount-${b.id}`}>{inr(b.bonus_amount)}</p>
        <p className="text-xs text-white/35 mt-1">{b.percent}% · {fmtDate(b.created_at).split(",")[0]}</p>
      </div>
      <div className="col-span-4 flex flex-wrap items-center justify-end gap-2">
        {b.status === "credited" ? (
          <div className="text-xs text-emerald-400 text-right" data-testid={`bonus-credited-${b.id}`}>
            <p>Credited {fmtDate(b.credited_at)}{b.reference ? ` · Ref ${b.reference}` : ""}</p>
            <p className="text-white/35">by {b.credited_by}{b.proof && <> · <a href={imgUrl(b.proof)} target="_blank" rel="noopener noreferrer" data-testid={`bonus-proof-link-${b.id}`} className="text-[#D4AF37] hover:underline inline-flex items-center gap-1"><ImageIcon size={11} /> Proof</a></>}</p>
          </div>
        ) : (
          <>
            <input data-testid={`bonus-reference-${b.id}`} placeholder="UTR / reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} className={`${inputCls} w-40`} />
            <label data-testid={`bonus-proof-label-${b.id}`} className={`cursor-pointer border px-3 py-2 text-xs transition-colors ${file ? "border-[#D4AF37]/60 text-[#D4AF37]" : "border-white/10 text-white/50 hover:text-white"}`} title={file ? file.name : "Attach transfer screenshot"}>
              <Paperclip size={12} className="inline mr-1" />{file ? "Attached" : "Proof"}
              <input data-testid={`bonus-proof-input-${b.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <button data-testid={`bonus-credit-${b.id}`} disabled={busy} onClick={credit} className="border border-emerald-400/60 text-emerald-400 px-4 py-2 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-emerald-400 hover:text-black transition-colors disabled:opacity-50">
              {busy ? "…" : "Credit"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const ReferralBonuses = () => {
  const [rule, setRule] = useState(null);
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/settings/referral").then((r) => setRule(r.data)).catch(() => {});
    api.get("/admin/referral-bonuses").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  const pending = items.filter((b) => b.status === "pending");
  const replace = (b) => setItems((list) => list.map((x) => (x.id === b.id ? b : x)));
  return (
    <section className="mb-14 space-y-6" data-testid="referral-bonuses-section">
      {rule && <RuleCard rule={rule} onSaved={setRule} />}
      <AdminLeaderboard />
      <div>
        <div className="flex items-end justify-between mb-4">
          <h3 className="font-display text-xl tracking-tight">Referral Bonuses</h3>
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35" data-testid="bonus-pending-count">{pending.length} pending · {inr(pending.reduce((s, b) => s + b.bonus_amount, 0))} to credit</p>
        </div>
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-accent text-[10px] tracking-[0.2em] uppercase text-white/35">
            <span className="col-span-3">Referrer</span>
            <span className="col-span-3">Friend's first investment</span>
            <span className="col-span-2">Bonus</span>
            <span className="col-span-4 text-right">Status</span>
          </div>
          {items.map((b) => <BonusRow key={b.id} b={b} onCredited={replace} />)}
          {items.length === 0 && <p className="px-6 py-6 text-sm text-white/40" data-testid="bonus-empty">No referral bonuses yet. A bonus appears here automatically when a referred investor's first investment is approved.</p>}
        </div>
      </div>
    </section>
  );
};
